import { useEffect, useRef, useState, type FC } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/lib/routes';
import {
  CabinetWorkspaceFrame,
  ScientificGpsBlock,
} from '@/features/cabinet/CabinetWorkspaceFrame';
import { RoleAcademicConnectionsOverlay } from '@/features/communications';
import { teacherApi } from './api';
import { TeacherCabinetGrid } from './components/TeacherCabinetGrid';
import { TeacherCourseWorkspace } from './components/TeacherCourseWorkspace';
import { TeacherGradebookOverlay } from './components/TeacherGradebookOverlay';
import { TeacherScientificProjectsOverlay } from './components/TeacherScientificProjectsOverlay';
import { TeacherRequestsOverlay } from './components/TeacherRequestsOverlay';
import { TeacherAttestationOverlay } from './components/TeacherAttestationOverlay';
import { TeacherOfficialSourcesOverlay } from './components/TeacherOfficialSourcesOverlay';
import { SuperAiWorkspaceModal } from '@/features/super-ai';
import { CreateRequestEntry } from '@/features/request-gateway';
import { ResearchToolsPage } from '@/features/research-tools';
import { FileHubPage } from '@/features/file-hub';
import { CourseMeetings } from '@/features/course/CourseMeetings';
import type {
  TeacherAssignment,
  TeacherAttendanceRecord,
  TeacherAttestationEntry,
  TeacherDiscipline,
  TeacherGradeRecord,
  TeacherGroup,
  TeacherLessonWeek,
  TeacherPersonalFile,
  TeacherProfile,
  TeacherScientificProject,
  TeacherStudent,
  TeacherStudentRequest,
  TeacherSubmission,
  TeacherWorkspaceSummary,
} from './types';

const CARD_PATHS: Record<string, string> = {
  'course-workspace': 'course-workspace',
  gradebook: 'gradebook',
  connections: 'connections',
  tools: 'tools',
  'scientific-projects': 'scientific-projects',
  meetings: 'meetings',
  requests: 'requests',
  attestation: 'attestation',
  files: 'files',
  'official-sources': 'official-sources',
};

export const TeacherPage: FC = () => {
  const { notify } = useToast();
  const navigate = useNavigate();
  const backToCabinet = () => navigate(routes.teacher);

  const [isAIOpen, setIsAIOpen] = useState(false);
  const [isCreateRequestOpen, setIsCreateRequestOpen] = useState(false);

  const [summary, setSummary] = useState<TeacherWorkspaceSummary | null>(null);
  const [profile, setProfile] = useState<TeacherProfile | null>(null);
  const [disciplines, setDisciplines] = useState<TeacherDiscipline[]>([]);
  const [selectedDisciplineCode, setSelectedDisciplineCode] = useState<string>('');
  const [groups, setGroups] = useState<TeacherGroup[]>([]);
  const [selectedGroupCode, setSelectedGroupCode] = useState<string>('');
  const [students, setStudents] = useState<TeacherStudent[]>([]);
  const [weeks, setWeeks] = useState<TeacherLessonWeek[]>([]);
  const [assignments, setAssignments] = useState<TeacherAssignment[]>([]);
  const [submissions, setSubmissions] = useState<TeacherSubmission[]>([]);
  const [grades, setGrades] = useState<TeacherGradeRecord[]>([]);
  const [attendance, setAttendance] = useState<TeacherAttendanceRecord[]>([]);
  const [scientificProjects, setScientificProjects] = useState<TeacherScientificProject[]>([]);
  const [requests, setRequests] = useState<TeacherStudentRequest[]>([]);
  const [attestations, setAttestations] = useState<TeacherAttestationEntry[]>([]);
  const [, setFiles] = useState<TeacherPersonalFile[]>([]);

  const contextGeneration = useRef(0);
  const loadContext = async (code: string, requestedGroup?: string) => {
    const generation = ++contextGeneration.current;
    setSelectedDisciplineCode(code); setSelectedGroupCode(requestedGroup || '');
    setGroups([]); setStudents([]); setWeeks([]); setAssignments([]); setSubmissions([]);
    setGrades([]); setAttendance([]); setAttestations([]);
    if (!code) return;
    try {
      const grps = await teacherApi.getGroups(code);
      if (generation !== contextGeneration.current) return;
      const group = requestedGroup || grps[0]?.group_code || '';
      setGroups(grps); setSelectedGroupCode(group);
      const [st, w, a, sub, gb, att, statement] = await Promise.all([
        group ? teacherApi.getStudents(code, group) : Promise.resolve([]),
        teacherApi.getWeeks(code), teacherApi.getAssignments(code, group || undefined),
        teacherApi.getSubmissions(undefined, code, group || undefined),
        group ? teacherApi.getGradebook(code, group) : Promise.resolve([]),
        group ? teacherApi.getAttendance(code, group) : Promise.resolve([]),
        group ? teacherApi.getAttestation(code, group) : Promise.resolve([]),
      ]);
      if (generation !== contextGeneration.current) return;
      setStudents(st); setWeeks(w); setAssignments(a); setSubmissions(sub);
      setGrades(gb); setAttendance(att); setAttestations(statement);
    } catch (e) {
      if (generation === contextGeneration.current) notify(e instanceof Error ? e.message : 'Не удалось загрузить учебный контекст');
    }
  };
  useEffect(() => {
    let active = true;
    void teacherApi.getSummary().then(async (sum) => {
      if (!active) return;
      setSummary(sum); setProfile(sum.profile); setDisciplines(sum.disciplines || []);
      setScientificProjects(sum.scientific_projects || []); setRequests(sum.recent_requests || []);
      setFiles(sum.personal_files || []);
      await loadContext(sum.disciplines?.[0]?.code || '');
    }).catch(() => { if (active) notify('Не удалось загрузить данные кабинета преподавателя'); });
    return () => { active = false; contextGeneration.current++; };
  }, []);
  const handleSelectDiscipline = (code: string) => loadContext(code);
  const handleSelectGroup = (groupCode: string) => loadContext(selectedDisciplineCode, groupCode);

  const handleSaveGrades = async (newGrades: TeacherGradeRecord[]) => {
    const generation = contextGeneration.current;
    const saved = await teacherApi.saveGradebook({
      discipline_code: selectedDisciplineCode,
      group_code: selectedGroupCode,
      grades: newGrades.map((g) => ({
        student_name: g.student_name,
        student_user_id: g.student_user_id,
        week_number: g.week_number,
        lecture_score: g.lecture_score,
        practice_score: g.practice_score,
        sro_score: g.sro_score,
      })),
    });
    if (generation !== contextGeneration.current) return;
    setGrades(saved);
    notify('Баллы электронного журнала сохранены');
  };

  const handleSaveAttendance = async (newAttendance: TeacherAttendanceRecord[]) => {
    const generation = contextGeneration.current;
    const saved = await teacherApi.saveAttendance({
      discipline_code: selectedDisciplineCode,
      group_code: selectedGroupCode,
      records: newAttendance.map((a) => ({
        student_name: a.student_name,
        student_user_id: a.student_user_id,
        week_number: a.week_number,
        lesson_type: a.lesson_type,
        status: a.status,
      })),
    });
    if (generation !== contextGeneration.current) return;
    setAttendance(saved);
    notify('Отметки посещаемости успешно зафиксированы');
  };

  const handleCreateDiscipline = async (payload: {
    code: string;
    title: string;
    credits: number;
    degree_level: string;
  }) => {
    const created = await teacherApi.createDiscipline(payload);
    setDisciplines((prev) => [...prev, created]);
    await loadContext(created.code);
    notify(`Дисциплина ${created.code} успешно создана`);
  };

  const handleCreateAssignment = async (payload: Partial<TeacherAssignment>) => {
    const generation = contextGeneration.current;
    const created = payload.id ? await teacherApi.editAssignment(payload.id, payload) : await teacherApi.createAssignment(payload);
    if (generation !== contextGeneration.current) return;
    setAssignments((prev) => [...prev.filter(a => a.id !== created.id), created]);
    notify(`Задание «${created.title}» сохранено`);
  };

  const handleGradeSubmission = async (submissionId: string, score: number | null, feedback: string, status = 'оценено') => {
    const generation = contextGeneration.current;
    const updated = await teacherApi.gradeSubmission(submissionId, { score, feedback, status });
    if (generation !== contextGeneration.current) return;
    setSubmissions((prev) => prev.map((s) => (s.id === submissionId ? updated : s)));
    const [journal, statement] = await Promise.all([teacherApi.getGradebook(selectedDisciplineCode, selectedGroupCode), teacherApi.getAttestation(selectedDisciplineCode, selectedGroupCode)]);
    if (generation !== contextGeneration.current) return;
    setGrades(journal); setAttestations(statement);
    notify(status === 'доработка' ? 'Работа возвращена с комментарием' : `Работа проверена. Выставлен балл: ${score}`);
  };

  const handleUpdateWeekContent = async (payload: Partial<TeacherLessonWeek> & { discipline_code: string; week_number: number }) => {
    const generation = contextGeneration.current;
    const updated = await teacherApi.updateWeek(payload);
    if (generation !== contextGeneration.current) return;
    setWeeks((prev) => [...prev.filter(w => w.week_number !== payload.week_number), updated].sort((a,b) => a.week_number-b.week_number));
    notify(`Материалы недели ${payload.week_number} обновлены`);
  };

  const handleCreateScientificProject = async (payload: {
    title: string;
    direction?: string;
    stage_title?: string;
    stage_timeline?: string;
  }) => {
    const created = await teacherApi.createScientificProject(payload);
    setScientificProjects((prev) => [...prev, created]);
    notify(`Научный проект «${created.title}» создан`);
  };

  const handleAddProjectParticipant = async (projectId: string, studentName: string, groupCode: string) => {
    const updated = await teacherApi.addProjectParticipant(projectId, {
      student_name: studentName,
      group_code: groupCode,
      student_code: '6B01717',
      program_name: 'Казахский язык и литература',
      project_role: 'Участник исследовательской группы',
    });
    setScientificProjects((prev) => prev.map((p) => (p.id === projectId ? updated : p)));
    notify(`Обучающийся ${studentName} добавлен в проект`);
  };

  const handleRemoveProjectParticipant = async (projectId: string, studentName: string) => {
    const updated = await teacherApi.removeProjectParticipant(projectId, studentName);
    setScientificProjects((prev) => prev.map((p) => (p.id === projectId ? updated : p)));
    notify(`Обучающийся ${studentName} удален из проекта`);
  };

  const handleReplyRequest = async (requestId: string, replyText: string, status?: string) => {
    const updated = await teacherApi.replyRequest(requestId, { reply_text: replyText, new_status: status });
    setRequests((prev) => prev.map((r) => (r.id === requestId ? updated : r)));
    notify('Ответ на обращение обучающегося отправлен');
  };

  const handleGenerateStatement = async (disciplineCode: string, groupCode: string) => {
    const records = await teacherApi.generateAttestation(disciplineCode, groupCode);
    setAttestations(records);
    notify(`Ведомость для группы ${groupCode} сформирована`);
  };


  const handleCardClick = (cardId: string) => {
    const path = CARD_PATHS[cardId];
    if (path) navigate(path);
  };

  const openCourseWorkspace = () => navigate('course-workspace');

  const cabinetIndex = (
    <CabinetWorkspaceFrame
      links={{
        notebook: routes.portalSection('materials'),
        calendar: routes.teacherModule('meetings'),
        meetings: routes.teacherModule('meetings'),
        connections: routes.teacherModule('connections'),
        alliances: routes.teacherModule('scientific-projects'),
        qa: routes.portalSection('instructions'),
        support: routes.portalSection('contacts'),
      }}
    >
      <div className="w-full space-y-8">
        <TeacherCabinetGrid
          summary={summary}
          profile={profile}
          onOpenCard={handleCardClick}
          onOpenAI={() => setIsAIOpen(true)}
          onOpenCreateRequest={() => setIsCreateRequestOpen(true)}
        />
      </div>
      <ScientificGpsBlock openTo={routes.teacherModule('scientific-projects')} />
    </CabinetWorkspaceFrame>
  );

  return (
    <div className="min-h-screen bg-[#060c18] p-4 text-slate-200 sm:p-6 lg:p-8 font-sans role-cabinet-page">
      <Routes>
        <Route path="connections" element={<RoleAcademicConnectionsOverlay isOpen roleContext="TEACHER" roleTitle="Преподаватель" onClose={backToCabinet} />} />
        <Route index element={cabinetIndex} />

        <Route
          path="course-workspace"
          element={
            <TeacherCourseWorkspace
              disciplines={disciplines}
              selectedDisciplineCode={selectedDisciplineCode}
              onSelectDiscipline={handleSelectDiscipline}
              groups={groups}
              selectedGroupCode={selectedGroupCode}
              onSelectGroup={handleSelectGroup}
              students={students}
              weeks={weeks}
              assignments={assignments}
              submissions={submissions}
              profile={profile}
              onBackToCabinet={backToCabinet}
              onSaveWorkspace={() => void loadContext(selectedDisciplineCode, selectedGroupCode)}
              onCreateDiscipline={handleCreateDiscipline}
              onCreateAssignment={handleCreateAssignment}
              onGradeSubmission={handleGradeSubmission}
              onUpdateWeekContent={handleUpdateWeekContent}
            />
          }
        />

        <Route
          path="gradebook"
          element={
            <TeacherGradebookOverlay
              disciplines={disciplines}
              selectedDisciplineCode={selectedDisciplineCode}
              onSelectDiscipline={handleSelectDiscipline}
              groups={groups}
              selectedGroupCode={selectedGroupCode}
              onSelectGroup={handleSelectGroup}
              students={students}
              initialGrades={grades}
              initialAttendance={attendance}
              profile={profile}
              onBackToCabinet={backToCabinet}
              onOpenCourseWorkspace={openCourseWorkspace}
              onSaveGrades={handleSaveGrades}
              onSaveAttendance={handleSaveAttendance}
            />
          }
        />

        <Route
          path="tools/*"
          element={<ResearchToolsPage onClose={backToCabinet} onBack={backToCabinet} />}
        />

        <Route
          path="scientific-projects"
          element={
            <TeacherScientificProjectsOverlay
              projects={scientificProjects}
              profile={profile}
              onBackToCabinet={backToCabinet}
              onSaveWorkspace={() => notify('Изменения сохранены')}
              onCreateProject={handleCreateScientificProject}
              onAddParticipant={handleAddProjectParticipant}
              onRemoveParticipant={handleRemoveProjectParticipant}
            />
          }
        />

        <Route path="meetings" element={<><button onClick={backToCabinet}>В кабинет</button><CourseMeetings teacher code={selectedDisciplineCode} group={selectedGroupCode} students={students} /></>} />
        <Route path="meetings/*" element={<><button onClick={backToCabinet}>В кабинет</button><CourseMeetings teacher code={selectedDisciplineCode} group={selectedGroupCode} students={students} /></>} />

        <Route
          path="requests"
          element={
            <TeacherRequestsOverlay
              requests={requests}
              profile={profile}
              onBackToCabinet={backToCabinet}
              onOpenCourseWorkspace={openCourseWorkspace}
              onReplyRequest={handleReplyRequest}
            />
          }
        />

        <Route
          path="attestation"
          element={
            <TeacherAttestationOverlay
              disciplines={disciplines}
              selectedDisciplineCode={selectedDisciplineCode}
              onSelectDiscipline={handleSelectDiscipline}
              groups={groups}
              selectedGroupCode={selectedGroupCode}
              onSelectGroup={handleSelectGroup}
              attestations={attestations}
              profile={profile}
              onBackToCabinet={backToCabinet}
              onOpenCourseWorkspace={openCourseWorkspace}
              onGenerateStatement={handleGenerateStatement}
            />
          }
        />

        <Route
          path="files/*"
          element={<FileHubPage onClose={backToCabinet} onBack={backToCabinet} />}
        />

        <Route
          path="official-sources"
          element={
            <TeacherOfficialSourcesOverlay
              profile={profile}
              onBackToCabinet={backToCabinet}
              onOpenCourseWorkspace={openCourseWorkspace}
            />
          }
        />
      </Routes>

      <SuperAiWorkspaceModal
        isOpen={isAIOpen}
        onClose={() => setIsAIOpen(false)}
        activeRole="TEACHER"
      />

      <CreateRequestEntry
        isOpen={isCreateRequestOpen}
        onClose={() => setIsCreateRequestOpen(false)}
        onBack={() => setIsCreateRequestOpen(false)}
        activeRole="TEACHER"
        roleLabel="Преподаватель"
      />
    </div>
  );
};

export default TeacherPage;
